import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { sendLetter } from "../lib/hearts";

const TOPICS = [
  ["hello", "Just saying hello"],
  ["painting", "About a painting"],
  ["use", "Using a painting"],
];
const field = "w-full rounded-xl border border-[#7E57C2]/25 bg-white px-4 py-2.5 font-display text-lg text-[#2D1E3E] outline-none transition-colors duration-200 focus:border-[#7E57C2]";
const Hint = ({ id, text }) => (
  <p data-testid={`letter-hint-${id}`} role="alert" className="mt-1 pl-2 font-display text-sm italic text-[#A2456A]">
    {text}
  </p>
);

export function LetterForm({ paintings = [], inline = false }) {
  const [open, setOpen] = useState(inline);
  const [f, setF] = useState({ name: "", email: "", message: "", topic: "hello", painting_slug: "", website: "" });
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");
  const [bad, setBad] = useState({});
  const check = (v) => ({
    ...(v.name.trim() ? {} : { name: "Please add your name" }),
    ...(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.email.trim()) ? {} : { email: v.email.trim() ? "That email doesn't look quite right" : "Please add your email so Priyanka can reply" }),
    ...(v.message.trim().length >= 2 ? {} : { message: "Please write a few words to Priyanka" }),
  });
  const edit = (k) => (e) => {
    const next = { ...f, [k]: e.target.value };
    setF(next);
    if (bad[k]) setBad((b) => ({ ...b, [k]: check(next)[k] }));
  };
  useEffect(() => {
    const on = (e) => {
      setF((o) => ({ ...o, ...e.detail }));
      setState("idle");
      setOpen(true);
    };
    window.addEventListener("afae-letter", on);
    return () => window.removeEventListener("afae-letter", on);
  }, []);
  const send = async (e) => {
    e.preventDefault();
    setErr("");
    const problems = check(f);
    setBad(problems);
    if (Object.keys(problems).length) return;
    setState("sending");
    try {
      await sendLetter({ ...f, painting_slug: f.painting_slug || null });
      setState("sent");
    } catch (x) {
      setState("idle");
      const d = x.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Please check your name, email and letter, then try again.");
    }
  };
  const body = (
    <motion.form data-testid="letter-form" noValidate onSubmit={send} onClick={(e) => e.stopPropagation()} initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 10, opacity: 0 }} className="flex w-full max-w-lg flex-col gap-4 rounded-[2rem] bg-[#FBF7F2] px-7 py-7 shadow-[0_30px_80px_-30px_rgba(45,30,62,0.6)]">
      {state === "sent" ? (
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <p data-testid="letter-sent" className="font-display text-2xl italic text-[#2D1E3E]">Your letter is on its way to Priyanka.</p>
          {!inline && (
            <button type="button" data-testid="letter-close" onClick={() => setOpen(false)} className="rounded-full border border-[#7E57C2]/40 px-5 py-2 font-display text-lg text-[#4A2D55] hover:bg-white">
              Close
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="flex items-baseline justify-between">
            <h2 className="font-display text-3xl italic text-[#2D1E3E]">Write to Priyanka</h2>
            {!inline && (
              <button type="button" data-testid="letter-close" onClick={() => setOpen(false)} className="font-display text-base text-[#5C4A72] hover:text-[#2D1E3E]">
                Close
              </button>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <input data-testid="letter-name" maxLength={80} placeholder="Your name" aria-invalid={!!bad.name} value={f.name} onChange={edit("name")} className={`${field} ${bad.name ? "border-[#C2577A]/70" : ""}`} />
              {bad.name && <Hint id="name" text={bad.name} />}
            </div>
            <div>
              <input data-testid="letter-email" type="email" placeholder="Your email" aria-invalid={!!bad.email} value={f.email} onChange={edit("email")} className={`${field} ${bad.email ? "border-[#C2577A]/70" : ""}`} />
              {bad.email && <Hint id="email" text={bad.email} />}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <select data-testid="letter-topic" value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} className={field}>
              {TOPICS.map(([v, t]) => (
                <option key={v} value={v}>
                  {t}
                </option>
              ))}
            </select>
            <select data-testid="letter-painting" value={f.painting_slug} onChange={(e) => setF({ ...f, painting_slug: e.target.value })} className={field}>
              <option value="">No particular painting</option>
              {paintings.map((p) => (
                <option key={p.slug} value={p.slug}>
                  {p.title}
                </option>
              ))}
            </select>
          </div>
          <div>
            <textarea data-testid="letter-message" maxLength={1500} rows={6} placeholder="Dear Priyanka," aria-invalid={!!bad.message} value={f.message} onChange={edit("message")} className={`${field} font-hand text-xl leading-snug ${bad.message ? "border-[#C2577A]/70" : ""}`} />
            {bad.message && <Hint id="message" text={bad.message} />}
          </div>
          <input tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="letter-website" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className="absolute -left-[9999px] h-0 w-0 opacity-0" />
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-[#5C4A72]">{f.message.length} / 1500</span>
            <button data-testid="letter-send" disabled={state === "sending"} className="rounded-full bg-[#4A2D55] px-6 py-2.5 font-display text-lg text-white transition-colors duration-200 hover:bg-[#2D1E3E] disabled:opacity-60">
              {state === "sending" ? "Sending…" : "Send the letter"}
            </button>
          </div>
          {err && (
            <p data-testid="letter-error" className="text-sm text-[#9B2C4E]">
              {err}
            </p>
          )}
        </>
      )}
    </motion.form>
  );
  if (inline) return body;
  return (
    <AnimatePresence>
      {open && (
        <motion.div data-testid="letter-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} className="fixed inset-0 z-[96] flex items-center justify-center overflow-y-auto bg-[#2D1E3E]/30 p-4 backdrop-blur-sm">
          {body}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
